import { pushError } from "../monitoring/faro";

const isDevelopment = import.meta.env.DEV;
const LOG_ENDPOINT = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/logs`
  : null;

// ─── Log buffer ──────────────────────────────────────────────────────────────
// Logs are batched in memory and flushed every 5 seconds or when the buffer
// reaches 20 entries. On page unload, the buffer is flushed synchronously via
// navigator.sendBeacon so no logs are lost.

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_BATCH_SIZE = 20;

let buffer = [];
let flushTimer = null;

const getAppContext = () => ({
  app: "pmtoolbox",
  env: import.meta.env.VITE_APP_ENV ?? "production",
  version: import.meta.env.VITE_APP_VERSION ?? "unknown",
  url: window.location.pathname,
});

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushBuffer();
  }, FLUSH_INTERVAL_MS);
}

function flushBuffer(useBeacon = false) {
  if (!LOG_ENDPOINT || buffer.length === 0) return;

  const entries = buffer.splice(0, buffer.length);
  const payload = JSON.stringify({ logs: entries });

  if (useBeacon && navigator.sendBeacon) {
    // sendBeacon survives page unload; no auth header possible here,
    // so NestJS must accept unauthenticated beacon calls from same origin.
    navigator.sendBeacon(LOG_ENDPOINT, new Blob([payload], { type: "application/json" }));
    return;
  }

  // Use fetch keepalive for normal flushes (survives tab close on most browsers)
  fetch(LOG_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Silently drop on network failure — logs are best-effort
  });
}

function pushToBuffer(level, message, context = {}) {
  if (!LOG_ENDPOINT) return; // dev / log endpoint not configured

  buffer.push({
    level,
    message,
    timestamp: new Date().toISOString(),
    context: { ...getAppContext(), ...context },
  });

  if (buffer.length >= FLUSH_BATCH_SIZE) {
    clearTimeout(flushTimer);
    flushTimer = null;
    flushBuffer();
  } else {
    scheduleFlush();
  }
}

// Flush remaining logs before the tab closes
window.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") flushBuffer(true);
});
window.addEventListener("pagehide", () => flushBuffer(true));

// ─── Public API ──────────────────────────────────────────────────────────────

function createLogger(module = "") {
  const tag = module ? `[${module}]` : "";

  return {
    log: (...args) => {
      if (isDevelopment) {
        console.log(`[LOG]${tag}`, ...args);
      }
    },

    error: (...args) => {
      console.error(`[ERROR]${tag}`, ...args);

      const err = args.find((a) => a instanceof Error) ?? new Error(String(args[0]));
      // Faro captures errors with full stack trace and Web Vitals context
      pushError(err);
      // Also ship to Loki via NestJS for centralised log search
      pushToBuffer("error", err.message, {
        ...(module && { module }),
        stack: err.stack?.slice(0, 500) ?? "",
      });
    },

    warn: (...args) => {
      if (isDevelopment) {
        console.warn(`[WARN]${tag}`, ...args);
      }
      pushToBuffer("warn", args.map(String).join(" "), module ? { module } : {});
    },

    info: (...args) => {
      if (isDevelopment) {
        console.info(`[INFO]${tag}`, ...args);
      }
      pushToBuffer("info", args.map(String).join(" "), module ? { module } : {});
    },

    debug: (...args) => {
      if (isDevelopment) {
        console.debug(`[DEBUG]${tag}`, ...args);
      }
    },

    /** Returns a child logger scoped to a sub-module, e.g. logger.forModule("billing").forModule("export") */
    forModule: (subModule) => createLogger(module ? `${module}:${subModule}` : subModule),
  };
}

export const logger = createLogger();

/**
 * Create a module-scoped logger. Module name is included in every log entry
 * shipped to Loki, making it easy to filter with {module="billing"} in Grafana.
 *
 * Usage (one line at the top of each file):
 *   const log = logger.forModule("billing");
 *   log.error("Failed to fetch invoice");
 */
export const forModule = (module) => createLogger(module);
